import { lazy, Suspense, type ReactNode } from 'react';
import {
  BrowserRouter,
  MemoryRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DexToaster } from '@dexmate/dexui';
import { buildTarget } from '@/lib/auth';
import { AppLayout } from '@/components/AppLayout';

// Routes are lazy-loaded so the dexhub bundle can defer this whole tree.
// Naming aligns with eng-review delta "Frontend bundle weight" FYI item.
const OperatorDashboard = lazy(() => import('./routes/index'));
const MyTrajectories = lazy(() => import('./routes/trajectories/index'));
const TrajectoryDetail = lazy(() => import('./routes/trajectories/[id]'));
const AdminThroughput = lazy(() => import('./routes/admin/throughput'));
const AdminOperators = lazy(() => import('./routes/admin/operators/index'));
const AdminOperatorDetail = lazy(() => import('./routes/admin/operators/[id]'));
const AdminIntegrity = lazy(() => import('./routes/admin/integrity'));
const AdminColdStorage = lazy(() => import('./routes/admin/cold-storage'));
const MLSearch = lazy(() => import('./routes/search/index'));
const MLTrajectoryViewer = lazy(() => import('./routes/search/[id]'));
const AnnotationQueue = lazy(() => import('./routes/annotation/index'));
const AnnotationWorkspace = lazy(() => import('./routes/annotation/[id]'));
const Tokens = lazy(() => import('./routes/tokens'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, err) => {
        if ((err as { status?: number }).status === 401) return false;
        return failureCount < 2;
      },
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

interface AppShellProps {
  /** When mounted inside dexhub, dexhub already provides a Router. */
  embedded?: boolean;
  /** dexhub-only: optionally override the location for SSR/routing harness. */
  initialPath?: string;
  children?: ReactNode;
}

function DataCollectionRoutes({ embedded = false }: { embedded?: boolean }) {
  // The standalone build wraps every route in AppLayout (sidebar + header).
  // The dexhub-module build is mounted under dexhub's own AppLayout, so we
  // skip ours and just render the route Outlet bare.
  const Shell = embedded ? RouteShell : AppLayout;

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route element={<RouteShell />}>
          {/* Bare-domain visit (standalone only): redirect to /data so users
              don't see a blank screen. Dexhub-mounted routing only forwards
              /data/* to us, so this redirect is a no-op there. */}
          <Route index element={<Navigate to="/data" replace />} />
          <Route path="/data" element={<OperatorDashboard />} />
          <Route path="/data/trajectories" element={<MyTrajectories />} />
          <Route path="/data/trajectories/:id" element={<TrajectoryDetail />} />
          <Route path="/data/admin" element={<AdminThroughput />} />
          <Route path="/data/admin/throughput" element={<AdminThroughput />} />
          <Route path="/data/admin/operators" element={<AdminOperators />} />
          <Route path="/data/admin/operators/:id" element={<AdminOperatorDetail />} />
          <Route path="/data/admin/integrity" element={<AdminIntegrity />} />
          <Route path="/data/admin/cold-storage" element={<AdminColdStorage />} />
          <Route path="/data/search" element={<MLSearch />} />
          <Route path="/data/search/:id" element={<MLTrajectoryViewer />} />
          <Route path="/data/annotation" element={<AnnotationQueue />} />
          <Route path="/data/annotation/:id" element={<AnnotationWorkspace />} />
          <Route path="/data/tokens" element={<Tokens />} />
          {/* Catch-all: unknown URLs route to the dashboard rather than
              blank-screening. */}
          <Route path="*" element={<Navigate to="/data" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

function RouteShell() {
  return (
    <Suspense fallback={<div className="p-6 text-dex-text-secondary">Loading…</div>}>
      <Outlet />
    </Suspense>
  );
}

/**
 * Shell wrapper. The standalone build sets up its own BrowserRouter + Query
 * client; the dexhub-module build expects dexhub to provide both and we just
 * mount the routes.
 */
export function App({ embedded, initialPath }: AppShellProps) {
  const isEmbedded = embedded ?? buildTarget() === 'dexhub-module';

  if (isEmbedded) {
    return <DataCollectionRoutes embedded />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <DataCollectionRoutes />
      </BrowserRouter>
      {/* Standalone-only: mount the toast surface. The dexhub-module build
          inherits dexhub's own toaster, so we don't double-mount. */}
      <DexToaster />
    </QueryClientProvider>
  );
}

/** Useful for tests + Storybook: mount with a specific path under MemoryRouter. */
export function AppForTest({ initialPath = '/data' }: { initialPath?: string }) {
  return (
    <QueryClientProvider client={new QueryClient()}>
      <MemoryRouter initialEntries={[initialPath]}>
        <DataCollectionRoutes embedded />
      </MemoryRouter>
    </QueryClientProvider>
  );
}
